'use client'

import { useEffect, useRef } from 'react'
import { Container, Lead, Body, Reveal, Section } from 'design-studio'
import Image from 'next/image'

const WORDS = (
  'Discover and refine a living mathematical model of your system, ' +
  'grounded in code, tests, and observed behavior.'
).split(' ')

// dim: teal-600 (#0d9488); lit: teal-50 (#f0fdfa)
const DIM: [number, number, number, number] = [13, 148, 136, 1.0]
const LIT: [number, number, number, number] = [240, 253, 250, 1.0]

function wordColor(t: number): string {
  const c = Math.max(0, Math.min(1, t))
  const r = Math.round(DIM[0] + (LIT[0] - DIM[0]) * c)
  const g = Math.round(DIM[1] + (LIT[1] - DIM[1]) * c)
  const b = Math.round(DIM[2] + (LIT[2] - DIM[2]) * c)
  const a = +(DIM[3] + (LIT[3] - DIM[3]) * c).toFixed(3)
  return `rgba(${r},${g},${b},${a})`
}

export function SectionTagline() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const wordRefs = useRef<(HTMLSpanElement | null)[]>([])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return

    const update = () => {
      const rect = el.getBoundingClientRect()
      const vh = window.innerHeight
      // Replicates offset: ['start 0.9', 'center 0.5']:
      // progress = 0 when element top is at 90% vh; 1 when element center is at 50% vh
      const range = rect.height / 2 + vh * 0.4
      const progress = Math.max(0, Math.min(1, (vh * 0.9 - rect.top) / range))
      const wordIdx = progress * WORDS.length

      wordRefs.current.forEach((span, i) => {
        if (!span) return
        const t = (wordIdx - (i - 0.5)) / 1
        span.style.color = wordColor(t)
      })
    }

    window.addEventListener('scroll', update, { passive: true })
    update()
    return () => window.removeEventListener('scroll', update)
  }, [])

  return (
    <Section id="tagline" className="space-y-9 py-16 sm:py-24">
      <Container ref={sectionRef} className="relative z-10 max-w-6xl">
        <Reveal>
          <Body
            size="md"
            tone="muted"
            className="max-w-2xl text-left text-lg lg:text-3xl"
          >
            {WORDS.map((word, i) => (
              <span
                key={i}
                ref={(el) => {
                  wordRefs.current[i] = el
                }}
                style={{ color: wordColor(0) }}
              >
                {word}
                {i < WORDS.length - 1 ? ' ' : ''}
              </span>
            ))}
          </Body>
        </Reveal>
      </Container>
      <Container className="relative z-10 max-w-6xl">
        <Reveal>
          <Image
            src="/images/dashboard/layout-dark.png"
            alt="SpecLogician dashboard preview — state instances sidebar, summary tiles, and reachability"
            width={3656}
            height={2004}
            className="w-full rounded-lg shadow-2xl ring-1 shadow-blue-950/60 ring-white/10"
          />
        </Reveal>
      </Container>
    </Section>
  )
}
